import React, {useEffect, useState} from 'react';
import {Card, Space, Image, Avatar, DatePicker, Modal, Input, Checkbox, Table, Select} from 'antd';
import './index.css';
import {Col, Container, Row, UncontrolledPopover, PopoverHeader, PopoverBody, FormGroup, Label} from 'reactstrap';
import "flatpickr/dist/themes/material_green.css";
import axios from "axios";
import {useCredentialStore} from "../../../hooks/useCredentialsStore";
import {
    getChargePointBasedOnTextSearchBody,
    getChargePointBasedOnTextSearchResponse,
    transaction,
    transactionResponse
} from "../../../types/admin";
import {PopNotification} from "../../../shared/notification";
import moment from "moment";
import {milliToHHMMSS} from "../../../utils/dateUtils";
import {UserTransactionActions} from "./UserTransactionActions";

const getMonthList = (num: number) => {
    const arr = ["all"]
    for (let i = 0; i < num; i++) {
        // startOf('month') keeps the 29th-31st from rolling over into the following month
        arr.push(moment().startOf('month').subtract(i, 'month').format('MM-YYYY'))
    }

    return arr;
}

const csvCell = (value: any) => {
    if (value === null || value === undefined) {
        return '';
    }

    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const UserTransaction: React.FC = ({children}) => {

    const [exporting, setExporting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [transactionDataSource, setTransactionDataSource] = useState<transaction[]>();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true, //this line allows the dropdown to show to choose the number of items per page
        pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'] //set the options of items per page selecting (pagination dropdown)
    });
    const [chargePointList, setChargePointList] = useState<getChargePointBasedOnTextSearchBody[]>();
    const [tenantList, setTenantList] = useState<any[]>();
    const [selectedChargePoint, setSelectedChargePoint] = useState("all");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const {token} = useCredentialStore(({token}) => ({
        token
    }));

    const monthList = getMonthList(12);
    const [selectedMonth, setSelectedMonth] = useState<string>("all")
    const [selectedTenant, setSelectedTenant] = useState<any>("all")

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const columns = [
        {
            title: 'Charger Unique Id',
            dataIndex: 'chargerUniqueId',
            key: 'chargerUniqueId',
            render: (text: string) => <span>{text ?? "-"}</span>
        },
        {
            title: 'Socket Unique Id',
            dataIndex: 'socketUniqueId',
            key: 'socketUniqueId',
            render: (text: string) => <span>{text ?? "-"}</span>
        },
        {
            title: 'Started at',
            dataIndex: 'startedAt',
            key: 'startedAt',
            render: (text: string) => <div>{text ? moment.utc(text).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : ''}</div>
        },
        {
            title: 'Stopped at',
            dataIndex: 'stoppedAt',
            key: 'stoppedAt',
            render: (text: string) => <div>{text ? moment.utc(text).utcOffset(8).format('YYYY-MM-DD HH:mm:ss') : ''}</div>
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            render: (text: string) => <div>{text ? milliToHHMMSS(text) : ''}</div>
        },
        {
            title: 'Energy charged (kWh)',
            dataIndex: 'energyCharged',
            key: 'energyCharged',
            render: (text: number) => <div>{text?.toFixed(2)}</div>
        },
        {
            title: 'Tenant',
            dataIndex: 'tenant',
            key: 'tenant'
        },
        {
            title: 'User ID',
            dataIndex: 'userId',
            key: 'userId',
        },
        {
            title: 'Transaction ID',
            dataIndex: 'transactionId',
            key: 'transactionId',
        },
        {
            title: 'Ocpi Session ID',
            dataIndex: 'ocpiSessionId',
            key: 'ocpiSessionId'

        },
        {
            title: 'Earnings (RM)',
            dataIndex: 'earnings',
            key: 'earnings',
            render: (text: number) => <div>{text?.toFixed(2)}</div>
        },
        {
            title: 'Tenant Rate',
            dataIndex: 'tenantRate',
            key: 'tenantRate',
            render: (text: number) => <div>{text?.toFixed(4)}</div>
        },
        {
            title: 'Actual Earnings (RM)',
            dataIndex: 'actualEarnings',
            key: 'actualEarnings',
            render: (text: number) => <div>{text?.toFixed(2)}</div>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Error',
            dataIndex: 'errorReason',
            key: 'errorReason'
        },
        {
            title: 'Actions',
            dataIndex: 'status',
            key: 'actions',
            render: (text: string, record: any) => {
                return <div><UserTransactionActions status={text} paymentId={record.transactionId}/></div>
            }
        }
    ];

    const onGetTransaction = (page: number, pageSize: number) => {
        setLoading(true);
        axios.get(`/api/v2/transaction?chargePointId=${selectedChargePoint}&page=${page - 1}&pageSize=${pageSize}&tenantId=${selectedTenant}&month=${selectedMonth}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then(res => {
            const response: transactionResponse = res.data;
            setTransactionDataSource(response.data);
            setPagination({
                ...pagination,
                current: page,
                pageSize: pageSize,
                total: response.totalElements
            })
        }).catch((err) => {
            PopNotification("error", err?.response?.data?.message || `Something went wrong, ${err.toString()}`);
        }).finally(() => {
            setLoading(false);
            setIsModalVisible(false);
        })
    }

    const onGetChargePointList = () => {
        axios.get("/api/chargePointsLocationSocketStatusSummary?page=0&pageSize=1000", {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then(res => {
            const response: getChargePointBasedOnTextSearchResponse = res.data;
            const list = [
                {
                    createdTime: 1,
                    id: {id: 'all'},
                    imageId: {id: null},
                    isOperative: true,
                    isPublic: true,
                    method: 'rate',
                    name: 'all',
                    privilegeRate: 0,
                    rate: 0,
                    tariff: 0
                },
                ...response.data
            ]
            setChargePointList(list);
            setSelectedChargePoint(list[0].id.id);
        }).catch((err) => {
            PopNotification("error", err?.response?.data?.message || `Something went wrong, ${err.toString()}`);
        })
    }

    const onExportCsv = () => {
        if (!token || exporting) {
            return;
        }

        setExporting(true);
        axios.get(`/api/v2/transaction/csv?chargePointId=${selectedChargePoint}&page=${pagination.current - 1}&pageSize=${pagination.pageSize}&tenantId=${selectedTenant}&month=${selectedMonth}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((res) => {
            const rows: string[][] = res.data ?? [];
            const csv = rows.map(row => row.map(csvCell).join(',')).join('\r\n');
            const blob = new Blob([`\uFEFF${csv}`], {type: 'text/csv;charset=utf-8;'});
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `transactions-${selectedMonth}-${selectedChargePoint}-${selectedTenant}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }).catch((err) => {
            PopNotification("error", err?.response?.data?.message || `Something went wrong, ${err.toString()}`);
        }).finally(() => {
            setExporting(false);
        })
    }

    const onGetTenantList = () => {
        axios.get("/api/tenant?page=0&pageSize=1000", {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then(res => {
            const response: getChargePointBasedOnTextSearchResponse = res.data;
            const list = [
                {
                    id: 'all',
                    name: 'all',
                },
                {
                    id: 'rex',
                    name: 'rex'
                },
                ...response.data
            ]
            setTenantList(list);
            setSelectedTenant(list[0].id);
        }).catch((err) => {
            PopNotification("error", err?.response?.data?.message || `Something went wrong, ${err.toString()}`);
        })
    }

    useEffect(() => {
        onGetTransaction(pagination.current, pagination.pageSize)
        onGetChargePointList()
        onGetTenantList()
    }, []);

    useEffect(() => {
        onGetTransaction(1, pagination.pageSize)
    }, [selectedChargePoint, selectedMonth, selectedTenant]);

    function handleTableChange(pagination: any) {
        onGetTransaction(pagination.current, pagination.pageSize)
    }

    return (
        <div>
            <Container fluid={true} style={{marginTop: 20}}>
                <Row style={{marginBottom: 20}}>
                    <Col md={8}>
                        <span className='page-title' style={{marginBottom: 0}}>Charge Point | Transactions</span>
                    </Col>
                    <Col md={4} style={{textAlign: "right"}}>
                        <span className='d-flex' style={{justifyContent: "flex-end", alignItems: "center"}}>
                            <span
                                onClick={onExportCsv}
                                style={{
                                    backgroundColor: "#39B64B",
                                    marginTop: 2,
                                    padding: "5px 15px",
                                    cursor: exporting ? "wait" : "pointer",
                                    color: "white",
                                    borderRadius: 7,
                                    display: "inline-block",
                                    textTransform: "uppercase",
                                    marginRight: 10
                                }}>
                                {exporting ? 'Exporting...' : 'Export As csv'}
                            </span>
                        </span>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <span>Charge Point</span>
                        <Select style={{width: "100%"}} value={selectedChargePoint}
                                onChange={(value) => {
                                    setSelectedChargePoint(value)
                                }}>
                            {
                                chargePointList?.map((chargePoint, index) => {
                                        return (
                                            <Select.Option value={chargePoint.id.id}>{chargePoint.name}</Select.Option>
                                        )
                                    }
                                )
                            }
                        </Select>
                    </Col>
                    <Col col={4}>
                        <span>Tenant</span>
                        <Select style={{width: "100%"}} value={selectedTenant}
                                onChange={(value) => {
                                    setSelectedTenant(value)
                                }}>
                            {
                                tenantList?.map((tenant, index) => {
                                        return (
                                            <Select.Option value={tenant.id}>{tenant.name}</Select.Option>
                                        )
                                    }
                                )
                            }
                        </Select>
                    </Col>
                    <Col col = {4}>
                        <span>Month</span>
                        <Select style={{width: "100%"}} value={selectedMonth}
                                onChange={(value) => {
                                    setSelectedMonth(value)
                                }}>
                            {
                                monthList?.map((month, index) => {
                                        return (
                                            <Select.Option value={month}>{month}</Select.Option>
                                        )
                                    }
                                )
                            }
                        </Select>
                    </Col>
                </Row>
                <Row md={12}>
                    <Col
                        md={12}
                        className='card-datavisulization'
                        style={{textAlign: "left", padding: 20, height: "80vh", overflowX: "auto"}}>
                        <Table
                            columns={columns}
                            dataSource={transactionDataSource}
                            loading={loading}
                            pagination={pagination}
                            onChange={handleTableChange}
                        />
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default UserTransaction
